import { TooltipTrigger } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useScheduleDrawer } from '~/stores/use-schedule-drawer/useScheduleDrawer';
import { useUserUpload } from '~/views/alfred/research/use-user-upload/useUserUpload';

import { ChatInputConfig } from '../../chat-input';
import * as S from './ChatActionButtons.styles';

export interface ChatActionButtonsProps {
  isMessagePending: boolean;
  inputValue: string;
  abortRequest?: () => void;
  config?: ChatInputConfig;
}

export const ChatActionButtons = ({
  isMessagePending,
  inputValue,
  abortRequest,
  config,
}: ChatActionButtonsProps) => {
  const { t } = useTranslation('chat');
  const isAlfredEverywhere = config?.alfredEverywhere;
  const openScheduleDrawer = useScheduleDrawer(state => state.open);

  const isScheduledTaskEnabled = useFlags(
    state => state.getFeatureFlagState('scheduled_task_feature')
  );

  const { uploadingFiles, hasUploadedFiles } = useUserUpload(state => ({
    uploadingFiles: state.files.filter(file => file.status === 'uploading'),
    hasUploadedFiles: state.files.length > 0,
  }));

  const hasEmptyInput = !inputValue.trim();
  const hasUploadingFiles = uploadingFiles.length > 0;
  const isSubmitButtonDisabled =
    hasEmptyInput ||
    hasUploadingFiles ||
    (isAlfredEverywhere && isMessagePending);

  const isScheduleButtonDisabled = hasEmptyInput || hasUploadedFiles;

  const handleScheduleClick = () => {
    openScheduleDrawer();
  };

  const getScheduleTooltipLabel = () => {
    if (hasUploadedFiles) {
      return t('chat:scheduleTaskDisabledWithFile');
    }

    if (hasEmptyInput) {
      return t('chat:scheduleTaskDisabledNoInput');
    }

    return t('chat:historySidebar.scheduleTask');
  };

  const submitButton = (
    <TooltipContainer
      label={t('chat:stopResponse')}
      inPortal
      trigger={[TooltipTrigger.Hover]}
      disabled={!isMessagePending}
    >
      {isMessagePending && !isAlfredEverywhere ? (
        <S.SubmitButton
          type="button"
          data-testid="abort-button"
          iconName="Stop"
          onClick={abortRequest}
        />
      ) : (
        <S.SubmitButton
          type="submit"
          data-testid="submit-button"
          variant="primary"
          iconName="ArrowUp"
          disabled={isSubmitButtonDisabled}
        />
      )}
    </TooltipContainer>
  );

  if (!isScheduledTaskEnabled) {
    return submitButton;
  }

  return (
    <>
      <S.ActionButtonsContainer>
        <TooltipContainer
          label={getScheduleTooltipLabel()}
          inPortal
          trigger={[TooltipTrigger.Hover]}
        >
          <S.ScheduleButton
            type="button"
            data-testid="schedule-task-button"
            variant="tertiary"
            iconName="Clock"
            disabled={isScheduleButtonDisabled}
            onClick={handleScheduleClick}
          />
        </TooltipContainer>

        {submitButton}
      </S.ActionButtonsContainer>
    </>
  );
};
